import React, { useEffect } from "react";
import { isClarityReady } from "./clarity";
import { trackClarityUiClick } from "./trackClarityApi";

const CLICKABLE_SELECTOR =
  "button, [role='button'], a, input[type='submit'], input[type='button'], .MuiButtonBase-root";

function resolveClickLabel(el: HTMLElement): {
  label: string;
  testId?: string;
  href?: string;
} {
  const testId =
    el.getAttribute("data-analytics") ||
    el.getAttribute("data-testid") ||
    undefined;
  const aria = el.getAttribute("aria-label") || undefined;
  const title = el.getAttribute("title") || undefined;
  const name =
    el instanceof HTMLInputElement || el instanceof HTMLButtonElement
      ? el.name || undefined
      : undefined;
  const href =
    el instanceof HTMLAnchorElement
      ? el.getAttribute("href") || undefined
      : undefined;

  let text = (el.innerText || el.textContent || "").replace(/\s+/g, " ").trim();
  if (text.length > 60) {
    text = `${text.slice(0, 57)}...`;
  }

  const label =
    testId || aria || title || name || text || el.tagName.toLowerCase();
  return { label, testId: testId || undefined, href };
}

function isDisabledControl(el: HTMLElement): boolean {
  if (el.getAttribute("aria-disabled") === "true") return true;
  if (el.hasAttribute("disabled")) return true;
  if (el.classList.contains("Mui-disabled")) return true;
  if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
    return el.disabled;
  }
  return false;
}

/**
 * Capture-phase document listener — tracks button/link clicks across all flows
 * (product, category, order, education, membership, auth, etc.) without per-screen wiring.
 */
const ClarityClickListener: React.FC = () => {
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (!isClarityReady()) return;
      const target = event.target;
      if (!(target instanceof Element)) return;

      const el = target.closest(CLICKABLE_SELECTOR);
      if (!(el instanceof HTMLElement)) return;
      if (isDisabledControl(el)) return;

      const { label, testId, href } = resolveClickLabel(el);
      if (!label || label === "div" || label === "span") return;

      trackClarityUiClick(label, { testId, href });
    };

    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return null;
};

export default ClarityClickListener;
